import React, { useMemo } from 'react';
import Shield from '../../shared/components/Shield';
import TrafficButtons from '../../shared/components/TrafficButtons';
import PercentageBox from '../../shared/components/PercentageBox';
import IconBox from '../../shared/components/IconBox';
import { useNavigate } from 'react-router-dom';
import Indicator from '../../shared/components/Indicator';
import ScrollingText from '../../shared/components/ScrollingText';
import { useMobile } from '../../contexts/MobileContext';
import { Pill, fraudPillColors } from '../../shared/components/tableUtils';

import { ReactComponent as DragHandleIcon } from '../../assets/icons/dragHandle.svg';
import { ReactComponent as BotIcon } from '../../assets/icons/bot.svg';

const OverviewSiteCard = ({ site, dragHandleProps }) => {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const botClicks = Number(site?.botClicks || 0);
  const humanClicks = Number(site?.humanClicks || 0);
  const totalClicks = humanClicks + botClicks;
  const status = site?.status || null;
  const fraudLevel = site?.fraudLevel || null;
  const hasFraudLevel = Boolean(fraudLevel);
  const showFraudLevel = hasFraudLevel && status !== 'red';
  const clickPercentage = totalClicks > 0 ? (botClicks / totalClicks) * 100 : undefined;
  const statusLabel = ({
    disabled: 'Disabled',
    detection_only: 'Monitoring',
    true_traffic: 'Active',
  })[site?.detectionLevel] || 'Active';
  const percentage = useMemo(() => {
    if (site.previousHumanClicks === null) return null;

    const previousTotal = Number(site.previousHumanClicks) + Number(site.previousBotClicks);
    if (previousTotal === 0 || totalClicks === 0) return null;

    const oldBotPercentage = Number(site.previousBotClicks) / previousTotal;
    const newBotPercentage = botClicks / totalClicks;
    return (newBotPercentage - oldBotPercentage) * 100;
  }, [botClicks, totalClicks, site.previousHumanClicks, site.previousBotClicks]);

  return (
    <div className={`site-card site-card--tile${isMobile ? ' site-card--no-drag' : ''}`}>
      {!isMobile && (
        <div {...dragHandleProps} className="site-card__drag drag-handle">
          <DragHandleIcon className="icon xs drag-handle-icon" />
        </div>
      )}

      <div className="site-card__header">
        <div className="site-card__site site-card__site-link"
             onClick={() => navigate('/dashboard/site/' + site.website)}>
          <IconBox website={site.website} />
          <ScrollingText className="site-card__name" text={site.website} />
        </div>
        <Indicator className="site-card__indicator" site={site} />
      </div>

      <div className="site-card__panels">
        <div className="site-card__panel site-card__panel--status">
          <div className="site-card__panel-header">
            <span className="site-card__eyebrow subtle-label">Status</span>
            <TrafficButtons direction="row" site={site}/>
          </div>
          <span className="site-card__text site-card__text--status">{statusLabel}</span>
        </div>

        <div className="site-card__panel site-card__panel--fraud">
          <div className="site-card__panel-header">
            <span className="site-card__eyebrow subtle-label">Fraud Level</span>
            {showFraudLevel && (
              <Shield fraudLevel={fraudLevel}
                      className="icon base site-card__metric-icon" />
            )}
          </div>
          {status === null && !hasFraudLevel && (
            <span className="site-card__empty">Unavailable</span>
          )}
          {status === 'red' && (
            <span className="site-card__empty">Waiting for traffic...</span>
          )}
          {showFraudLevel && (
            <div className="site-card__fraud-content">
              {(() => {
                const { bg, fg } = fraudPillColors(fraudLevel);
                return (
                  <Pill
                    text={fraudLevel}
                    bg={bg}
                    fg={fg}
                  />
                );
              })()}
              <span className="site-card__text">bot activity</span>
            </div>
          )}
        </div>

        <div className="site-card__panel site-card__panel--traffic">
          <div className="site-card__panel-header">
            <span className="site-card__eyebrow subtle-label">Bot Traffic</span>
            {status !== 'red' && (
              <BotIcon className="icon base site-card__metric-icon site-card__metric-icon--bot" />
            )}
          </div>
          {status === 'red' && <span className="site-card__empty">-</span>}
          {status !== 'red' && (
            <>
              <div className="site-card__value-row">
                <span className="metric-value-primary">
                  {botClicks.toLocaleString()}
                </span>
                {clickPercentage !== undefined && (
                  <>
                    <span className="metric-value-separator"> | </span>
                    <span className="metric-value-secondary">{clickPercentage.toFixed(2)}%</span>
                  </>
                )}
              </div>
              <div className="site-card__support">
                <PercentageBox percentage={percentage} bot />
                <span className="site-card__support-text">vs previous period</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OverviewSiteCard;
